const CONFIRMATIONS = {
  docue_builder_finalize_draft: { action: 'finalize-draft', target: 'draft', destructive: false },
  docue_delete_document: { action: 'delete-document', target: 'document', destructive: true },
  docue_delete_document_folder: { action: 'delete-folder', target: 'folder', destructive: true },
  docue_delete_document_reminder: { action: 'delete-reminder', target: 'document', destructive: true },
  docue_send_document_to_email: { action: 'send-email', target: 'document', destructive: false },
  docue_signing_cancel_signee: { action: 'cancel-signee', target: 'document', destructive: true },
  docue_signing_send_invitations: { action: 'send-invitations', target: 'document', destructive: false },
  docue_update_document_folder: { action: 'update-folder', target: 'folder', destructive: false },
} as const

export function docueAgentToolConfirmation(toolName: string) {
  const name = toolName.startsWith('docue__') ? toolName.slice('docue__'.length) : toolName
  return Object.hasOwn(CONFIRMATIONS, name) ? CONFIRMATIONS[name as keyof typeof CONFIRMATIONS] : undefined
}

export function docueAgentToolInput(input: unknown): Record<string, unknown> {
  return input && typeof input === 'object' && !Array.isArray(input) ? input as Record<string, unknown> : {}
}

export interface DocueAgentConfirmationField {
  label: string
  value: string
}

type Translate = (key: string) => string

/** Only show the target, recipients and essential choices for this action. */
export function docueAgentConfirmationDetails(toolName: string, toolInput: unknown, t: Translate) {
  const input = docueAgentToolInput(toolInput)
  const action = docueAgentToolConfirmation(toolName)?.action
  const fields: DocueAgentConfirmationField[] = []
  const recipients: { name: string, contacts: string[] }[] = []
  const notices: string[] = []
  const text = (key: string) => t(`docueAgent.confirmation.${key}`)
  const string = (value: unknown) => typeof value === 'string' ? value.trim() : ''
  const field = (label: string, value: unknown) => {
    if (typeof value === 'string' && value.trim())
      fields.push({ label: text(label), value })
  }
  const contacts = (...values: unknown[]) => values.map(string).filter(Boolean)

  if (action === 'finalize-draft') {
    const method = string(input.signature_method)
    field('signature-method', ['manual', 'canvas', 'strong'].includes(method) ? text(`method-${method}`) : method)
    if (method !== 'manual') {
      field('invitations', text(input.send_invitations === true ? 'invitations-now' : 'invitations-later'))
      for (const value of Array.isArray(input.signees) ? input.signees : []) {
        const signee = docueAgentToolInput(value)
        recipients.push({
          name: [string(signee.first_name), string(signee.last_name)].filter(Boolean).join(' '),
          contacts: contacts(signee.email, signee.phone),
        })
      }
    }
  }

  if (action === 'send-email') {
    field('recipient', input.email)
    field('delivery', text(input.as === 'link' ? 'delivery-link' : 'delivery-attachment'))
  }

  if (action === 'delete-reminder')
    field('reminder', input.reminder_id)

  if (action === 'cancel-signee' || action === 'send-invitations') {
    const invitations = action === 'cancel-signee' ? [{ signee_id: input.signee_id }] : input.invitations
    for (const value of Array.isArray(invitations) ? invitations : []) {
      const invitation = docueAgentToolInput(value)
      recipients.push({
        name: string(invitation.signee_id),
        contacts: action === 'cancel-signee'
          ? []
          : contacts(
              invitation.email === undefined
                ? text('saved-email')
                : invitation.email === null ? text('no-email') : invitation.email,
              invitation.phone === undefined
                ? text('saved-phone')
                : invitation.phone === null ? text('no-sms') : invitation.phone,
            ),
      })
    }
    if (input.allow_out_of_order === true)
      notices.push(text('out-of-order-notice'))
  }

  if (action === 'update-folder') {
    field('new-name', input.name)
    field('destination-folder', input.parent_folder_id)
  }

  return { fields, recipients, notices }
}
